import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PublicLayout } from '../components/landing/PublicLayout';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Check } from 'lucide-react';

export default function PricingPage() {
  const { isAuthenticated, language } = useAuth();
  const navigate = useNavigate();

  const plans = [
    {
      name: t('pricing_free_name', language),
      price: t('pricing_free_price', language),
      description: t('pricing_free_desc', language),
      features: [
        t('pricing_free_f1', language),
        t('pricing_free_f2', language),
        t('pricing_free_f3', language),
        t('pricing_free_f4', language),
        t('pricing_free_f5', language),
      ],
      cta: t('pricing_start_free', language),
      highlighted: false,
    },
    {
      name: t('pricing_pro_name', language),
      price: t('pricing_pro_price', language),
      description: t('pricing_pro_desc', language),
      features: [
        t('pricing_pro_f1', language),
        t('pricing_pro_f2', language),
        t('pricing_pro_f3', language),
        t('pricing_pro_f4', language),
        t('pricing_pro_f5', language),
        t('pricing_pro_f6', language),
        t('pricing_pro_f7', language),
      ],
      cta: t('pricing_choose', language),
      highlighted: true,
    },
    {
      name: t('pricing_guild_name', language),
      price: t('pricing_guild_price', language),
      description: t('pricing_guild_desc', language),
      features: [
        t('pricing_guild_f1', language),
        t('pricing_guild_f2', language),
        t('pricing_guild_f3', language),
        t('pricing_guild_f4', language),
        t('pricing_guild_f5', language),
        t('pricing_guild_f6', language),
      ],
      cta: t('pricing_contact_sales', language),
      highlighted: false,
    },
  ];

  const faqs = [1, 2, 3, 4].map((n) => ({
    question: t(`pricing_faq_${n}_q`, language),
    answer: t(`pricing_faq_${n}_a`, language),
  }));

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {t('pricing_page_title', language)}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg">
            {t('pricing_page_subtitle', language)}
          </p>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="pb-20 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, i) => (
            <Card
              key={i}
              className={`relative flex flex-col ${
                plan.highlighted
                  ? 'ring-2 ring-primary border-primary shadow-lg scale-[1.02]'
                  : 'border-border/50'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  {t('pricing_popular', language)}
                </div>
              )}
              <CardContent className="p-6 flex flex-col flex-1">
                <h3 className="font-cinzel font-bold text-xl mb-1">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>

                <div className="mb-6">
                  <span className="font-cinzel text-4xl font-black text-foreground">
                    {plan.price === '0' ? (language === 'fr' ? 'Gratuit' : 'Free') : `${plan.price}€`}
                  </span>
                  {plan.price !== '0' && (
                    <span className="text-sm text-muted-foreground">{t('pricing_per_month', language)}</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature, fi) => (
                    <li key={fi} className="flex items-start gap-2.5 text-sm">
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={plan.highlighted ? 'fantasy-button w-full' : 'w-full'}
                  variant={plan.highlighted ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                >
                  {plan.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 px-4 bg-secondary/10">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-cinzel text-2xl sm:text-3xl font-bold text-center mb-10">
            {t('pricing_faq_title', language)}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </PublicLayout>
  );
}
